import { HttpClient } from "@angular/common/http";
import { EventEmitter, Injectable } from "@angular/core";
import { BaseDataService, HttpResponsesHandlersFactory, IHttpResponsesHandler } from "@bbwt/modules/data-service";
import { MessageService } from "primeng/api";
import * as signalR from "@microsoft/signalr";
import { Observable, Subject } from "rxjs";
import { Notification } from "./notification";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { NotificationOfflineStorage } from "./notification-offline.storage";

@Injectable({
    providedIn: "root"
})
export class NotificationService extends BaseDataService {
    private _onUpdated = new Subject<Notification[]>();

    protected _hubConnection: signalR.HubConnection;
    protected _onError = new Subject<string>();

    readonly url = "api/notifications";
    private readonly entityTitle = "Notification";

    private notifications: Notification[] = [];

    get newNotifications() {
        return this._onUpdated.asObservable();
    }

    get getNotificationError() {
        return this._onError.asObservable();
    }

    constructor(
        http: HttpClient,
        handlersFactory: HttpResponsesHandlersFactory,
        protected messageService: MessageService,
        private appOnlineStateService: AppOnlineStateService
    ) {
        super(http, handlersFactory);

        this._onUpdated = new EventEmitter<Notification[]>();

        this.initHubConnection();
    }

    public checkHubConnection() {
        if (this._hubConnection.state === signalR.HubConnectionState.Disconnected) {
            this._hubConnection.start();
        }
    }

    protected handleRequest<TResult>(request: Observable<TResult>, responseHandler?: IHttpResponsesHandler): Promise<TResult> {
        this.checkHubConnection();
        return super.handleRequest(request, responseHandler);
    }

    protected initHubConnection(): void {
        this._hubConnection = new signalR.HubConnectionBuilder()
            .withUrl("api/notification")
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.None)
            .build();

        this._hubConnection.onreconnected(() => {
            this.getAllNotifications().then(data => {
                this.notify(data);
            });
        });

        this._hubConnection.on("Unarchived", (unarchived: Notification[]) => {
            // Instead of just showing the notifications received from the server, we send a request so the cache gets updated
            this.getAllNotifications().then(data => {
                this.notify(data);
            });
        });

        this._hubConnection.on("Error", (errorMsg: string) => this._onError.next(errorMsg));

        this._hubConnection.start();
    }

    async notify(notifications: Notification[]) {
        this.notifications = notifications;
        this._onUpdated.next(await this.getAllNotifications());
    }

    getUnarchived(): Promise<Notification[]> {
        return this.handleRequest<Notification[]>(
            this.http.get<Notification[]>(`${this.url}/unarchived`),
            this.handlersFactory.getDefault()
        );
    }

    markAsRead(notification: Notification): Promise<Notification> {
        if (!notification.savedOffline) {
            return this.handleRequest<Notification>(this.http.put<Notification>(`${this.url}/mark-as-read/${notification.id}`, null))
                    .then(async response => {
                        this._onUpdated.next(await this.getAllNotifications());
                        return response;
                    });
        } else {
            notification.read = true;
            this.updateLocallyStoredItem(notification);
        }
    }

    markAllAsRead(): Promise<void> {
        return this.handleRequest<void>(
            this.http.put<void>(`${this.url}/read-all`, null),
            this.handlersFactory.getForUpdate(this.entityTitle)
        ).then(async _ => {
            this._onUpdated.next(await this.getAllNotifications());
        });
    }

    archive(id: string): Promise<Notification> {
        return this.handleRequest<Notification>(
            this.http.put<Notification>(`${this.url}/archive/${id}`, null),
            this.handlersFactory.getForUpdate(this.entityTitle)
        );
    }

    archiveAll(): Promise<void> {
        return this.handleRequest<void>(
            this.http.put<void>(`${this.url}/archive-all`, null),
            this.handlersFactory.getForUpdate(this.entityTitle)
        );
    }

    async createOffline(item: Notification): Promise<Notification> {
        item.savedOffline = true;
        item.id = null;
        const notification: Notification = NotificationOfflineStorage.save(item);
        this._onUpdated.next(await this.getAllNotifications());
        return notification;
    }

    async deleteOfflineNotification(id: string): Promise<Notification> {
        const item = NotificationOfflineStorage.remove(id);
        this._onUpdated.next(await this.getAllNotifications());
        return item;
    }

    private async updateLocallyStoredItem(item: Notification): Promise<Notification> {
        const notification = NotificationOfflineStorage.update(item);
        this._onUpdated.next(await this.getAllNotifications());
        return notification;
    }

    async getAllNotifications() {
        if (!this.appOnlineStateService.isAppOnline) {
            this.notifications = [];
        } else {
            await this.getUnarchived().then(data => {
                this.notifications = data;
            });
        }
        return this.mergeNotifications();
    }

    private mergeNotifications() {
        var offlineNotifications = NotificationOfflineStorage.getAll().map(n => {
            n.created = new Date(n.created);
            return n;
        });
        var allNotifications = this.notifications.concat(offlineNotifications);
        return this.sortNotifications(allNotifications);
    }

    private sortNotifications(notifications: Notification[]): Notification[] {
        return notifications.sort((a, b) => {
            if (a.created > b.created) {
                return -1;
            }
            if (a.created < b.created) {
                return 1;
            }
            return 0;
        });
    }
}
